"use client";

import { useState } from "react";
import { Button } from "@headlessui/react";
import { Play, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
// import demoVideo from "../../../public/videos/demo.mp4"
import heroSectionDemo from "../../assets/LandingPage/newLandingPage/heroSectionDemo.png";
import { FaDatabase } from "react-icons/fa6";
import { HiLightningBolt } from "react-icons/hi";
import { FaCloud } from "react-icons/fa6";

export default function Hero() {
  const [showVideo, setShowVideo] = useState(false);
  const navigate = useNavigate();

  const imageVariants = {
    hidden: { opacity: 0, x: 200, rotate: 10, scale: 0.8 },
    visible: {
      opacity: 1,
      x: 0,
      rotate: 0,
      scale: 1,
      transition: { duration: 2, ease: "easeOut" },
    },
  };


  const handleWatchDemo = () => {
    setShowVideo(true);

    setTimeout(() => {
      const video = document.getElementById("demo-video");
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.play().catch(() => {
          console.log("Autoplay blocked on mobile, user must tap play");
        });
      }
    }, 50);
  };
  return (
    <>
      <section
        className="
        min-h-[100svh]
        flex items-center
        bg-hero-gradient
        px-4 sm:px-6 lg:px-8
        pt-24 sm:pt-28 lg:pt-32
        pb-16 sm:pb-20
      "
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-12 items-center">
            {/* ================= LEFT ================= */}
            <div className="flex flex-col text-center lg:text-left">
              {/* Badge */}
              <div className="relative w-fit mx-auto lg:mx-0 mb-6">
                <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-secondary">
                  <span>⚡</span>
                  <span className="text-black text-sm font-bold">
                    AI-Powered Supplier Management
                  </span>
                </div>

                {/* Decorative SVG (desktop only) */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="569"
                  height="187"
                  viewBox="0 0 569 187"
                  fill="none"
                  className="
                    hidden lg:block
                    absolute
                    left-1/2
                    top-6
                    translate-x-1/4
                    pointer-events-none
                  "
                >
                  <path
                    d="M0 0.5L159 0.5L325.5 186L569 186"
                    stroke="url(#grad)"
                  />
                  <defs>
                    <linearGradient
                      id="grad"
                      x1="569"
                      y1="93"
                      x2="0"
                      y2="93"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="white" />
                      <stop offset="0.55" stopColor="white" stopOpacity="0" />
                      <stop offset="1" stopColor="white" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Heading */}
              <h1
                className="
                font-bold
                text-[clamp(2.25rem,5vw,3.75rem)]
                leading-[1.15]
                text-white
              "
              >
                Transform your <span className="text-[#EAF524]">Supplier</span>
                <br />
                <span className="bg-gradient-text bg-clip-text text-transparent">
                  Sourcing
                </span>
              </h1>

              {/* Description */}
              <p
                className="
                mt-6
                text-[clamp(1rem,2.5vw,1.5rem)]
                text-white
                leading-relaxed
                max-w-2xl
                mx-auto lg:mx-0
              "
              >
                SourceIQ connects buyers with suppliers through autonomous AI
                matching, streamlining procurement from discovery to performance
                monitoring.
              </p>

              {/* Buttons */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() => navigate("/signup")}
                  className="
                    w-full sm:w-auto
                    px-8 py-4
                    flex items-center justify-center gap-2
                    bg-secondary text-gray-900
                    rounded-lg font-medium
                    hover:opacity-90 transition
                  "
                >
                  Start Free Trial
                  <Play size={18} />
                </Button>

                <Button
                  onClick={handleWatchDemo}
                  className="
                    w-full sm:w-auto
                    px-8 py-4
                    rounded-lg
                    border border-white
                    text-white
                    hover:bg-white/10 transition
                  "
                >
                  Watch Demo
                </Button>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-6 justify-center lg:justify-start text-white text-sm sm:text-base">
                <div className="flex items-center gap-2 text-white text-base">
                  <FaDatabase />
                  <p>100K+ Pre-Screened Suppliers</p>
                </div>
                <div className="flex items-center gap-2 text-white text-base">
                  <HiLightningBolt />
                  <p>AI-Powered Matching</p>
                </div>
                <div className="flex items-center gap-2 text-white text-base">
                  <FaCloud />
                  <p>Cloud-Native Platform</p>
                </div>
              </div>
            </div>

            {/* ================= RIGHT ================= */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={imageVariants}
              className="flex justify-center lg:justify-end"
            >
              <div
                className="
                bg-white
                rounded-2xl
                shadow-2xl
                max-w-sm sm:max-w-md lg:max-w-lg
                max-h-[70svh]
                overflow-hidden
              "
              >
                <img
                  src={heroSectionDemo}
                  alt="Supplier Management Dashboard"
                  className="w-full h-full object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {showVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative" style={{ maxWidth: "900px", width: "100%" }}>
            {/* Close button floating on top-right */}
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-8 -right-8 text-white hover:text-gray-300 z-50"
            >
              <X size={40} />
            </button>

            {/* Storylane Embed */}
            <div
              className="sl-embed"
              style={{
                position: "relative",
                paddingBottom: "calc(50% + 25px)",
                width: "100%",
                maxWidth: "1000px",
                height: 0,
              }}
            >
              <iframe
                loading="lazy"
                className="sl-demo"
                src="https://app.storylane.io/demo/rfigd5k3ywzj?embed=inline"
                name="sl-embed"
                allow="fullscreen"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: "12px",
                  boxShadow: "0px 0px 18px rgba(0,0,0,0.25)",
                }}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



